import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  TextField,
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
} from "@mui/material";

const EditGallery = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [displayOrder, setDisplayOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [currentImageUrl, setCurrentImageUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const baseUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    const fetchImage = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${baseUrl}/auth/gallery/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const image = response.data;

        setTitle(image.title || "");
        setDescription(image.description || "");
        setDisplayOrder(image.displayOrder || 0);
        setIsActive(image.isActive !== undefined ? image.isActive : true);
        setCurrentImageUrl(image.imageUrl || "");

        if (image.imageUrl) {
          setImagePreview(`${baseUrl}${image.imageUrl}`);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching gallery image:", error);
        setError("Failed to load gallery image");
        setLoading(false);
      }
    };

    if (id) {
      fetchImage();
    }
  }, [id, baseUrl]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
      ];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!allowedTypes.includes(file.type)) {
        alert("Only JPG, JPEG, PNG, and GIF files are allowed.");
        e.target.value = "";
        return;
      }
      if (file.size > maxSize) {
        alert("Image size must be less than 5MB.");
        e.target.value = "";
        return;
      }

      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const validateForm = () => {
    if (!title.trim()) {
      alert("Title is required.");
      return false;
    }
    return true;
  };

  const handleUpdateImage = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("description", description);
      formData.append("displayOrder", displayOrder);
      formData.append("isActive", isActive);

      if (selectedImage) {
        formData.append("image", selectedImage);
      }

      try {
        const token = localStorage.getItem("token");
        await axios.put(`${baseUrl}/auth/gallery/${id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
        alert("Gallery image updated successfully");
        navigate("/dashboard/galleries");
      } catch (error) {
        console.error("Error updating gallery image:", error);
        alert(
          "Failed to update image: " +
            (error.response?.data?.error || "Unknown error"),
        );
      }
    }
  };

  const handleCancel = () => {
    navigate("/dashboard/galleries");
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Typography variant="h4" component="h1" sx={{ mt: 2 }} gutterBottom>
          Edit Gallery Image
        </Typography>
        <Typography>Loading image data...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Typography variant="h4" component="h1" sx={{ mt: 2 }} gutterBottom>
          Edit Gallery Image
        </Typography>
        <Alert severity="error">{error}</Alert>
        <Button
          variant="contained"
          onClick={() => navigate("/dashboard/galleries")}
          sx={{ mt: 2 }}
        >
          Back to Gallery
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" component="h1" sx={{ mt: 2 }} gutterBottom>
        Edit Gallery Image
      </Typography>
      <form onSubmit={handleUpdateImage}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              margin="normal"
              label="Title"
              variant="outlined"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              margin="normal"
              label="Description (Optional)"
              variant="outlined"
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
              Image
            </Typography>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{ margin: "8px 0", display: "block" }}
            />

            {currentImageUrl && !selectedImage && (
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 1 }}
                >
                  Current Image:
                </Typography>
                <img
                  src={`${baseUrl}${currentImageUrl}`}
                  alt="Current"
                  style={{
                    maxWidth: "300px",
                    maxHeight: "200px",
                    borderRadius: "8px",
                    display: "block",
                  }}
                  onError={(e) => {
                    e.target.src =
                      "https://via.placeholder.com/300x200/4B5563/FFFFFF?text=Image+Not+Found";
                  }}
                />
              </Box>
            )}

            {selectedImage && (
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 1 }}
                >
                  New Image Preview:
                </Typography>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    maxWidth: "300px",
                    maxHeight: "200px",
                    borderRadius: "8px",
                    display: "block",
                  }}
                />
              </Box>
            )}

            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              {selectedImage
                ? "New image selected"
                : "Leave empty to keep current image"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              margin="normal"
              label="Display Order"
              variant="outlined"
              type="number"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(parseInt(e.target.value) || 0)}
              helperText="Lower numbers appear first"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  color="primary"
                />
              }
              label="Active (Show in gallery)"
              sx={{ mt: 3 }}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, mb: 16, display: "flex", gap: 2 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{ width: "20%" }}
          >
            Update Image
          </Button>
          <Button
            type="button"
            variant="outlined"
            color="secondary"
            onClick={handleCancel}
            sx={{ width: "20%" }}
          >
            Cancel
          </Button>
        </Box>
      </form>
    </Container>
  );
};

export default EditGallery;
